// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Two hands, one cabinet, and a set of `PlayControls` at the end of it (M5-05, M5-07, M5-13).
//
// This is where all the pieces meet: the hands come in from `xrInput`, the placement says where
// the machine is, and what comes out is the same `PlayControls` object the keyboard fills. That
// last part is the point. There is **no XR path through the game**: the flippers, the plunger and
// the nudges all arrive at `PlaySession` through the one interface, get recorded into the one
// trace, and re-simulate in the validator with no idea a headset was involved. XR-021 records
// *which* input method a replay was played with so boards can be filtered by it; that is a label
// on the recording, not a different recording.
//
// **Confirmations are events, not state.** A press produces a `ConfirmEvent` on exactly the frame
// it happened, carrying everything the four confirmations need (06 §2.1): the sound to play, the
// haptic to fire, and which control to draw depressed. A caller that polled `pressed` instead
// would re-trigger the click every frame the button was held, which is a buzz rather than a
// click.
//
// **Comfort mode is the same rig with the reaching turned off.** 06 §6 asks for everything
// reachable without standing or leaning, and the temptation is a second input path. It is instead
// a flag: the gates take `advanceBound` instead of `advanceFingertip`, and every edge, sound,
// haptic and animation downstream is identical. A player who switches mid-ball notices nothing
// except that they no longer have to reach.

import type { PlayControls } from '../input/desktop'
import {
  cabinetControlsFor,
  onAControl,
  PLUNGER_GRAB_WRAP,
  inPlungerZone,
  PRESSABLE_IDS,
  type CabinetControls,
  type PressableId,
} from './cabinetControls'
import type { CabinetDimensions } from '../world/cabinet'
import { PressGate, type PressState } from './pressGate'
import { PLUNGE_DEADBAND, PlungerGrab, alongAxis } from './plungerGrab'
import { nudging, ShoveDetector, type NudgeImpulse } from './bodyNudge'
import { CabinetSpring } from './cabinetSpring'
import { CabinetGrab } from './cabinetGrab'
import { DROP_PULSE, GRAB_PULSE, Haptics, PRESS_PULSE, RELEASE_PULSE, SHOVE_PULSE } from './haptics'
import { InputMethodTracker, type MethodChange } from './inputMethod'
import { PinchCarry } from './build/pinchCarry'

/**
 * Where the rod's pinch closes: 25 mm, the line every pinch used before 1.0.235 (1.0.241). The
 * shared line moved to 15 mm so a resting hand stopped reading as a pinch on the Build bench; the
 * rod followed it, and on the owner's 26 Oct log three of nine takes let go at 0-6 mm within
 * 40-100 ms — fingers round a knob do not meet. The rod is gated by its zone. `CabinetRig.RodPinchClose`.
 */
export const ROD_PINCH_CLOSE = 0.025

/** How far a fist round the rod opens past its tightest to let go, metres (the 1.0.107 round). */
export const ROD_FIST_RELEASE = 0.02
import type { HandReport, HandTracker } from './xrInput'
import {
  CONTROLLER_LATENCY_SECONDS,
  HAND_LATENCY_SECONDS,
  ledX,
  ledY,
  ledZ,
  poseSpeed,
} from './poseTracking'
import { toCabinetDirection, toCabinetPoint, type MutablePoint, type Placement } from './placement'

/** Something the player did, on the frame they did it. */
export interface ConfirmEvent {
  readonly control: PressableId | 'plunger' | 'cabinet'
  readonly kind: 'press' | 'release' | 'plunge' | 'shove' | 'slam' | 'credit'
  /** 0 … 1, for a sound's gain and a haptic's strength. */
  readonly strength: number
}

/** What comfort mode binds, when the player is not reaching for anything. */
export interface BoundControls {
  readonly leftFlipper: boolean
  readonly rightFlipper: boolean
  readonly start: boolean
  /** Held to draw the plunger, released to fire — the A/X binding of 06 §6. */
  readonly plunge: boolean
  /** Thumbstick nudge, −1 … 1 on each axis. */
  readonly nudgeX: number
  readonly nudgeY: number
}

export const NO_BOUND_CONTROLS: BoundControls = {
  leftFlipper: false,
  rightFlipper: false,
  start: false,
  plunge: false,
  nudgeX: 0,
  nudgeY: 0,
}

/** How fast a bound plunge draws, metres per second of held button. Full travel in ~0.8 s. */
const BOUND_PLUNGE_RATE = 0.069

/** A thumbstick at full deflection is one keyboard nudge per this many seconds. */
const STICK_NUDGE_INTERVAL = 0.22
/** And it is worth this much, matching the keyboard's `NUDGE_IMPULSE`. */
const STICK_NUDGE_IMPULSE = 26

export interface CabinetRigOptions {
  readonly dimensions: CabinetDimensions
  /** Where the machine is standing. Replaced wholesale when the player re-places it. */
  placement: Placement
}

/**
 * The player, the cabinet, and everything between them.
 *
 * One per session. Holds a gate per control per hand — eight gates, which sounds like a lot until
 * you notice that a player really can press the left flipper button with either hand, and a rig
 * with one gate per control would have the second hand releasing the first hand's press.
 */
export class CabinetRig {
  readonly controls: CabinetControls
  readonly spring = new CabinetSpring()
  readonly method = new InputMethodTracker()
  /** The last scheme-change sentence, until somebody reads it. See `takeMethodToast`. */
  private methodToast: string | undefined

  /** Carrying the machine to a better height. Public so the placement UI can see a carry. */
  readonly grab = new CabinetGrab()

  placement: Placement

  /** Confirmations raised this frame. Drained by the caller; never reallocated. */
  readonly confirmations: ConfirmEvent[] = []

  private readonly gates = new Map<string, PressGate>()
  private readonly plunger = new PlungerGrab()
  private readonly shoves = new Map<string, ShoveDetector>()
  private readonly haptics = { left: new Haptics(), right: new Haptics() }
  /** Which hand is holding the plunger, so only that hand can move or drop it. */
  private plungerHand: 'left' | 'right' | undefined
  /**
   * Each hand's rod pinch (`PinchCarry`, the 1.0.107 round: "the hand has to be too far away to
   * release"): a pinch lets the rod go when it opens 12 mm past its tightest, not at the shared
   * 40 mm, and not only once the whole fist has opened as well.
   */
  private readonly rodPinch = { left: new PinchCarry(ROD_PINCH_CLOSE, true), right: new PinchCarry(ROD_PINCH_CLOSE, true) }
  /** How the rod was taken: by a pinch, or by a hand closed round it. */
  private rodByFist = false
  private rodFistLeast = 0
  /** A hand that has just let the rod go does not take it again by its fist until the fist has opened. */
  private readonly rodFistSpent = { left: false, right: false }
  /** The placement the held pull is measured in: a different one is a moved machine (25 Sep 2026). */
  private plungerFrame: Placement | undefined
  /** How many times a held pull was carried across a moved placement. `CabinetRig.PlungerRebased`. */
  plungerRebased = 0
  /** How long the hand holding a drawn rod has been out of tracking, seconds. */
  private plungerLostFor = 0
  private boundDraw = 0
  private stickCharge = 0
  /** The depression each control should be drawn at, 0 … 1. */
  private readonly depressions = new Map<PressableId, number>()
  /** The controls with a fingertip in their zone this frame, for the rail lamps. */
  private readonly overs = new Set<PressableId>()

  private readonly point: MutablePoint = { x: 0, y: 0, z: 0 }
  private readonly tipPoint: MutablePoint = { x: 0, y: 0, z: 0 }
  private readonly pinchPoint: MutablePoint = { x: 0, y: 0, z: 0 }
  private readonly palmPoint: MutablePoint = { x: 0, y: 0, z: 0 }
  /** Is a hand in the plunger's grab zone this frame, or holding it? The knob's light. */
  private plungerOverNow = false
  private readonly velocity: MutablePoint = { x: 0, y: 0, z: 0 }

  constructor(options: CabinetRigOptions) {
    this.controls = cabinetControlsFor(options.dimensions)
    this.placement = options.placement
    for (const hand of ['left', 'right'] as const) {
      for (const id of PRESSABLES) {
        this.gates.set(`${hand}:${id}`, new PressGate(this.controls[id]))
      }
      this.shoves.set(hand, new ShoveDetector())
    }
  }

  /** How far in a control is being pressed, for drawing it. */
  depressionOf(id: PressableId): number {
    return this.depressions.get(id) ?? 0
  }

  /** Is a fingertip in this control's zone, or the control down? The rail lamp's question. */
  overOf(id: PressableId): boolean {
    return this.overs.has(id)
  }

  /**
   * Is a hand on the machine right now?
   *
   * Asked by the venue before it teleports on a pinch: a player drawing the plunger has a hand
   * closed, and moving them to another bay for it would be the worst bug in the arcade.
   */
  get holdingMachine(): boolean {
    return this.plunger.held || this.depressions.size > 0
  }

  /** 0 … 1 plunger tension, for the rising pitch cue. */
  get plungerTension(): number {
    return this.plunger.tension
  }

  /**
   * How far back the knob is, metres. What the renderer draws.
   *
   * **This did not exist, and `plungerTension`'s own doc claimed the job** — "for the rising pitch
   * cue and the knob's position". The pitch cue was wired; the knob's position was not, in either
   * client, so a player could draw the plunger the full 55 mm and watch it not move. The Quest had
   * the property and no reader; the browser had neither.
   *
   * Separate from the tension rather than derived at the call site: tension is the same journey
   * expressed as 0 … 1, and multiplying it back by `PLUNGER_TRAVEL` in a renderer would put a
   * second copy of the travel somewhere that has no business owning one.
   */
  get plungerDrawn(): number {
    return this.plunger.drawn
  }

  /** Is a hand in the plunger's grab zone, or holding the rod? For lighting the knob. */
  get plungerOver(): boolean {
    return this.plungerOverNow || this.plunger.held
  }

  /** Is the rod held? */
  get plungerHeld(): boolean {
    return this.plunger.held
  }

  /**
   * One frame.
   *
   * `out` is the same `PlayControls` the keyboard writes into, and is mutated rather than
   * returned — the session reads flippers as *state* and consumes `launch` and `nudges` as
   * latched events, and building a fresh object each frame would lose the latch.
   */
  advance(
    hands: HandTracker,
    frameSeconds: number,
    out: PlayControls,
    options: {
      readonly comfort?: boolean
      readonly bound?: BoundControls
      /**
       * May a closed hand on a corner carry the cabinet? `false` while Build mode is on (M13-08):
       * the mode never moves the machine, and a hand at a corner post is reaching for a part. The
       * grab's latches are left as they are, so the first frame it runs again is not an edge.
       */
      readonly carry?: boolean
    } = {},
  ): void {
    this.confirmations.length = 0
    this.depressions.clear()
    this.overs.clear()
    this.plungerOverNow = false

    const previousMethod = this.method.method
    const change = this.method.advance(hands.trackedSources(), frameSeconds * 1000)
    if (change !== undefined) this.onMethodChange(change)
    else if(previousMethod!==this.method.method)this.releaseAll()

    this.haptics.left.attach(hands.left.actuator)
    this.haptics.right.attach(hands.right.actuator)

    // **The grab runs first and, while it holds, instead.** Carrying the machine must not also
    // shove it: a hand closed on the shell satisfies the nudge's contact test on every frame of
    // the carry, so without this a player rearranging their cabinet would nudge the ball across
    // the playfield the whole way and probably tilt. A grab is a placement gesture and the
    // simulation must not hear about it at all.
    if (options.carry !== false) {
      const carried = this.grab.advance(hands, this.placement, this.controls)
      if (this.grab.took || this.grab.dropped) {
        const which = this.grab.held === 'right' ? 'right' : 'left'
        this.haptics[which].fire(this.grab.took ? GRAB_PULSE : DROP_PULSE)
        this.confirmations.push({
          control: 'cabinet',
          kind: this.grab.took ? 'press' : 'release',
          strength: this.grab.took ? 0.8 : 0.35,
        })
      }
      if (this.grab.held !== undefined) {
        this.placement = carried
        this.releaseAll()
        return
      }
    } else if (this.grab.held !== undefined) {
      // Told mid-carry that carrying is off: let go where it is, as an input-method switch does.
      this.grab.release()
    }

    // Posture does not choose an input method. Preserve the tracker's established hand/controller
    // ownership and its grab-cancelling hysteresis, including while seated.
    const comfort = options.comfort === true && this.method.method === 'controllers'
    const bound = options.bound ?? NO_BOUND_CONTROLS

    if (comfort) this.advanceComfort(bound, frameSeconds, out)
    else {
      this.advanceHand(hands.left, frameSeconds, out)
      this.advanceHand(hands.right, frameSeconds, out)
    }

    out.leftFlipper = this.anyDown('flipperLeft') || (comfort && bound.leftFlipper)
    out.rightFlipper = this.anyDown('flipperRight') || (comfort && bound.rightFlipper)

    this.spring.advance(frameSeconds)
  }

  /** Let go of everything, because the session ended or the player took the headset off. */
  releaseAll(): void {
    for (const gate of this.gates.values()) gate.release()
    this.plunger.cancel()
    this.plungerHand = undefined
    this.boundDraw = 0
    this.stickCharge = 0
    for (const detector of this.shoves.values()) detector.reset()
    this.haptics.left.endRamp()
    this.haptics.right.endRamp()
  }

  /**
   * The sentence the last scheme change produced, read once (M9-38).
   *
   * **XR-008's confirming toast was built and dropped on the floor.** `InputMethodTracker` composes
   * it, `onMethodChange` took the change and used only `cancelGrabs`, and nothing anywhere else
   * read `.toast` — so on the browser a player who put a controller down and raised a hand got a
   * silently different control scheme, which 06 §2.5 makes the *whole* feedback for. The native
   * client has shown it since M7-13.
   *
   * Read-once rather than a field the host polls: a toast is a fact about a change, and one that
   * stayed set would be re-shown on every frame until the next switch.
   */
  takeMethodToast(): string | undefined {
    const said = this.methodToast
    this.methodToast = undefined
    return said
  }

  private onMethodChange(change: MethodChange): void {
    this.methodToast = change.toast
    // 06 §2.5: an in-progress grab is released safely rather than left stuck to an input source
    // that no longer exists. Cancelled, not fired — a player whose controller died mid-pull has
    // not asked for a plunge.
    if (change.cancelGrabs) {
      this.plunger.cancel()
      this.boundDraw = 0
      this.stickCharge = 0
      this.plungerHand = undefined
      this.grab.release()
      for (const gate of this.gates.values()) gate.release()
    }
  }

  private anyDown(id: PressableId): boolean {
    return (
      this.gates.get(`left:${id}`)?.pressed === true ||
      this.gates.get(`right:${id}`)?.pressed === true
    )
  }

  /** Let go of the rod: a plunge past the deadband, nothing under it. */
  private releasePlunger(out: PlayControls): void {
    const fired = this.plunger.release()
    if (fired === undefined) return
    out.launch = true
    out.launchPower = fired.power
    out.chargeStartedAt = null
    this.confirmations.push({ control: 'plunger', kind: 'plunge', strength: fired.power })
  }

  private advanceHand(hand: HandReport, frameSeconds: number, out: PlayControls): void {
    const side = hand.handedness
    const haptics = this.haptics[side]

    if (!hand.present) {
      for (const id of PRESSABLES) this.settle(side, id, this.gate(side, id).release(), out)
      if (this.plungerHand === side) {
        // **A drawn rod whose hand leaves tracking is let go, not dropped** (25 Sep 2026, the
        // build-88 round: "the plunger pulls but does not always hit the ball"). The headset log had
        // 23 of 65 pulls end "dropped at 55 mm — the hand holding it stopped being tracked", each
        // about 0.4 s after the take: the hand yanked back past the cameras' view at the moment it
        // opened. Dropping it was the rule for a controller that died mid-pull (06 §2.5); a hand
        // that leaves tracking at full draw is a hand letting go. A short grace keeps a one-frame
        // blip from firing a pull that is still being made; a rod under the deadband is dropped,
        // as before. `CabinetRig.cs` is the twin.
        if (this.plunger.drawn >= PLUNGE_DEADBAND) {
          this.plungerLostFor += frameSeconds
          if (this.plungerLostFor < PLUNGER_LOST_GRACE_SECONDS) {
            this.shoves.get(side)?.reset()
            return
          }
          this.releasePlunger(out)
        } else {
          this.plunger.cancel()
        }
        this.plungerLostFor = 0
        this.plungerHand = undefined
        haptics.endRamp()
      }
      this.shoves.get(side)?.reset()
      return
    }

    // XR-020's first mitigation: extrapolate the fingertip forward by the measured latency, so
    // contact registers when the finger *arrives* rather than when the report does.
    const lead = hand.kind === 'hands' ? HAND_LATENCY_SECONDS : CONTROLLER_LATENCY_SECONDS
    const led = this.point
    toCabinetPoint(
      this.placement,
      ledX(hand.tip, lead),
      ledY(hand.tip, lead),
      ledZ(hand.tip, lead),
      led,
    )

    for (const id of PRESSABLES) {
      const gate = this.gate(side, id)
      const state =
        hand.kind === 'hands'
          ? gate.advanceFingertip(led.x, led.y, led.z)
          : gate.advanceController(led.x, led.y, led.z, hand.trigger)
      this.settle(side, id, state, out)
    }

    this.advancePlunger(hand, side, led, out, haptics)
    this.advanceShove(hand, side, out)
    void frameSeconds
  }

  private advancePlunger(
    hand: HandReport,
    side: 'left' | 'right',
    led: MutablePoint,
    out: PlayControls,
    haptics: Haptics,
  ): void {
    const knob = this.controls.plunger
    // **The rod follows where the fingertip is, not where it is heading** (1.0.245): the led point
    // runs 40 ms ahead on the hand's speed, which suits a button and not a rod in the hand — a hand
    // flicking forward as it opened ran the rod back in early. `CabinetRig.cs` is the twin.
    const at = toCabinetPoint(this.placement, hand.tip.x, hand.tip.y, hand.tip.z, this.tipPoint)
    const along = alongAxis(knob.axis, at.x, at.y, at.z)
    // **A hand grabs, not a point** (14 Sep 2026). A pinch anywhere in the zone — fingertip,
    // pinch point or palm — or a closed hand whose palm is on the rod. Once held, only opening
    // the hand lets go; where it has moved to is the pull, not a reason to drop it.
    const closed = hand.closedMeasured && hand.closed
    const pinch = toCabinetPoint(this.placement, hand.pinch.x, hand.pinch.y, hand.pinch.z, this.pinchPoint)
    const palm = toCabinetPoint(this.placement, hand.palm.x, hand.palm.y, hand.palm.z, this.palmPoint)
    const inZone =
      inPlungerZone(knob, led.x, led.y, led.z)
      || inPlungerZone(knob, pinch.x, pinch.y, pinch.z)
      || inPlungerZone(knob, palm.x, palm.y, palm.z)
    const wrapped = inPlungerZone(knob, palm.x, palm.y, palm.z, PLUNGER_GRAB_WRAP)
    if (inZone) this.plungerOverNow = true
    // **The ruler's tight pinch counts beside the runtime's** (15 Sep 2026): a hand the runtime
    // has named gets its `grab` from `select`, which the headset does not fire for a pinch made
    // at waist height round a knob — the 01:37 tap had five millimetres between the tips and no
    // grab. See `HandReport.pinchTight`.
    const pinched = hand.grab || hand.pinchTight
    let grabbing = pinched || closed
    // The rod's own pinch, from the thumb-to-index gap where the fingers are measured.
    const rodPinch = this.rodPinch[side]
    const fingers = hand.kind === 'hands' && hand.grabMeasured && hand.lastPinchMetres > 0
    if (fingers) rodPinch.advance(true, hand.lastPinchMetres)
    else rodPinch.reset()
    if (!closed) this.rodFistSpent[side] = false

    if (this.plungerHand === side) {
      this.plungerLostFor = 0
      // **Held the way it was taken** (the 1.0.107 round). A pinch lets go when the pinch opens —
      // a curled hand round a knob reads as a fist, and the fist used to hold the rod on until the
      // fingers were spread wide; a fist lets go when it opens.
      if (fingers) {
        if (this.rodByFist) {
          this.rodFistLeast = Math.min(this.rodFistLeast, hand.lastFistMetres)
          grabbing = closed && hand.lastFistMetres < this.rodFistLeast + ROD_FIST_RELEASE
        } else {
          grabbing = rodPinch.closed
        }
      }
      if (!grabbing) {
        this.releasePlunger(out)
        this.plungerHand = undefined
        // Let go with the fingers still curled round the knob: not taken again by that fist.
        this.rodFistSpent[side] = closed
        haptics.endRamp()
        return
      }
      if (!samePlace(this.placement, this.plungerFrame)) {
        this.plunger.rebase(along)
        this.plungerFrame = this.placement
        this.plungerRebased += 1
      }
      // The draw stays where it was while the fingers open, so the pull fires at the length it
      // was held at, not wherever the opening hand dragged the fingertip.
      // The release looks back RELEASE_LOOKBACK_FRAMES for the pull (1.0.245), so a hand moving
      // forward as it opens does not take the pull back with it.
      if (!(fingers && !this.rodByFist && rodPinch.settling)) this.plunger.move(along)
      this.plunger.remember()
      haptics.ramp(this.plunger.tension)
      return
    }

    if (this.plungerHand !== undefined) return
    // A tracked hand takes by the rod's own pinch, which re-arms only after it has opened; the
    // shared one could still read closed the frame after a release and take the rod straight back.
    const pinchTakes = fingers ? rodPinch.closed : pinched
    const takes = (pinchTakes && inZone) || (closed && wrapped && !this.rodFistSpent[side])
    if (!takes) return
    if (this.plunger.grab(along)) {
      this.plungerHand = side
      this.plungerFrame = this.placement
      this.rodByFist = !(pinchTakes && inZone)
      this.rodFistLeast = hand.lastFistMetres
      // A pinch that took the rod is a closed rod pinch from here, whichever rule saw it first.
      if (fingers && !this.rodByFist && !rodPinch.closed) rodPinch.hold(hand.lastPinchMetres)
    }
  }

  private advanceShove(hand: HandReport, side: 'left' | 'right', out: PlayControls): void {
    const detector = this.shoves.get(side)
    if (detector === undefined) return
    // A hand that is not moving cannot shove anything, and a player's hands rest on the rails for
    // most of a game. Checking the speed before the two transforms below skips them on the
    // overwhelming majority of frames, which at two hands and 72 Hz is the difference between
    // 288 trigonometric calls a second and almost none.
    //
    // It returns rather than resetting: re-arming here would let a hand that shoved, slowed
    // against the cabinet and pushed again deliver two shoves without ever leaving it, which is
    // the one-shove-per-contact rule undone by an optimisation. A hand that genuinely leaves
    // re-arms on the next frame it moves, because that frame reaches `advance` and finds no
    // contact.
    if (!nudging(poseSpeed(hand.tip))) return
    // Position through the scale, velocity through the rotation only — see `placement.ts`.
    const at = toCabinetPoint(this.placement, hand.tip.x, hand.tip.y, hand.tip.z, this.point)
    // **A hand on a button is pressing the button, not shoving the machine** (M8-30). The grab
    // path has asked this since M7-13; this one never did, so more than half of a Quest session's
    // nudges were flipper presses. See `onAControl` for the measurement. Asked after the transform
    // because the controls are in cabinet space, and after the speed check because that is cheap.
    if (onAControl(this.controls, at.x, at.y, at.z)) return
    const v = toCabinetDirection(this.placement, hand.tip.vx, hand.tip.vy, hand.tip.vz, this.velocity)
    const shove = detector.advance(this.controls.body, at.x, at.y, at.z, v.x, v.y, v.z)
    if (shove === undefined) return
    this.applyShove(shove, out, this.haptics[side])
  }

  private applyShove(shove: NudgeImpulse, out: PlayControls, haptics: Haptics): void {
    out.nudges.push([shove.ix, shove.iy])
    this.spring.shove(shove.ix, shove.iy)
    const strength = Math.min(1, Math.hypot(shove.ix, shove.iy) / 34)
    haptics.fire(SHOVE_PULSE, strength)
    this.confirmations.push({
      control: 'cabinet',
      kind: shove.slam ? 'slam' : 'shove',
      strength,
    })
  }

  private advanceComfort(bound: BoundControls, frameSeconds: number, out: PlayControls): void {
    // Bound presses go through the same gates, so the edges, sounds and haptics are the same
    // ones a reaching player gets. Only the left hand's gates are used, arbitrarily and
    // harmlessly: in comfort mode there is one set of bindings, not one per hand.
    this.settle('left', 'flipperLeft', this.gate('left', 'flipperLeft').advanceBound(bound.leftFlipper), out)
    this.settle('left', 'flipperRight', this.gate('left', 'flipperRight').advanceBound(bound.rightFlipper), out)
    this.settle('left', 'start', this.gate('left', 'start').advanceBound(bound.start), out)

    // The plunger draws while the button is held and fires on release, which is the closest a
    // button can get to a pull. The rate is chosen so a full draw takes about as long as the
    // keyboard's (`PLUNGE_FULL_MS`), because a player switching between the two must not have to
    // relearn the skill shot.
    if (bound.plunge) {
      if (!this.plunger.held) this.plunger.grab(0)
      this.boundDraw += BOUND_PLUNGE_RATE * frameSeconds
      this.plunger.move(this.boundDraw)
      this.haptics.left.ramp(this.plunger.tension)
      this.haptics.right.ramp(this.plunger.tension)
    } else if (this.plunger.held) {
      const fired = this.plunger.release()
      this.boundDraw = 0
      this.haptics.left.endRamp()
      this.haptics.right.endRamp()
      if (fired !== undefined) {
        out.launch = true
        out.launchPower = fired.power
        out.chargeStartedAt = null
        this.confirmations.push({ control: 'plunger', kind: 'plunge', strength: fired.power })
      }
    }

    this.advanceStickNudge(bound, frameSeconds, out)
  }

  /**
   * The thumbstick nudge.
   *
   * Discrete taps rather than a continuous push, because a nudge *is* discrete on a real machine
   * — you shove it, it settles — and because a continuous impulse would let a seated player hold
   * the ball against a wall indefinitely. The rate is what a determined thumb can produce and is
   * comfortably inside what the tilt bob will forgive twice and not three times.
   */
  private advanceStickNudge(bound: BoundControls, frameSeconds: number, out: PlayControls): void {
    const magnitude = Math.min(1, Math.hypot(bound.nudgeX, bound.nudgeY))
    if (magnitude < 0.4) {
      this.stickCharge = 0
      return
    }
    this.stickCharge += frameSeconds
    if (this.stickCharge < STICK_NUDGE_INTERVAL) return
    this.stickCharge = 0
    const inv = 1 / Math.hypot(bound.nudgeX, bound.nudgeY)
    const ix = bound.nudgeX * inv * STICK_NUDGE_IMPULSE * magnitude
    const iy = bound.nudgeY * inv * STICK_NUDGE_IMPULSE * magnitude
    out.nudges.push([ix, iy])
    this.spring.shove(ix, iy)
    this.confirmations.push({ control: 'cabinet', kind: 'shove', strength: magnitude })
  }

  private gate(side: 'left' | 'right', id: PressableId): PressGate {
    // Constructed in the constructor for every pair, so this never misses.
    return this.gates.get(`${side}:${id}`) as PressGate
  }

  /**
   * One pressable's edges: the sound, the haptic, and — since M7-35 — the control itself.
   *
   * **`out` is new, and its absence was the defect.** This raised a confirmation for every press
   * and wrote nothing anybody could act on, so the start button and the coin slot *felt* like they
   * worked — the click and the buzz are real — while `PlaySession` never heard of either. Both
   * arrive here rather than beside their gates because this is the one place that knows a press
   * edge from a held finger, and comfort mode's bound start goes through it too.
   */
  private settle(side: 'left' | 'right', id: PressableId, state: PressState, out: PlayControls): void {
    if (state.depression > (this.depressions.get(id) ?? 0)) {
      this.depressions.set(id, state.depression)
    }
    if (state.over) this.overs.add(id)
    if (!state.pressedEdge && !state.releasedEdge) return
    const haptics = this.haptics[side]
    if (state.pressedEdge) {
      // **Latched, not held.** Both are decisions rather than positions: a held start would
      // restart a game every frame, and a held coin slot would fill the machine with credits for
      // as long as a finger rested on it.
      if (id === 'start') out.start = true
      else if (id === 'coin') out.coin = true
      haptics.fire(PRESS_PULSE)
      this.confirmations.push({
        control: id,
        // The coin slot is a gesture rather than a button (06 §2.4) — pressing it adds a credit
        // in free play, with the clunk, and the clunk is a different sound from a button click.
        kind: id === 'coin' ? 'credit' : 'press',
        strength: 1,
      })
      return
    }
    haptics.fire(RELEASE_PULSE)
    this.confirmations.push({ control: id, kind: 'release', strength: 0.3 })
  }
}

const PRESSABLES = PRESSABLE_IDS

/** The same placement, field by field: a re-stood machine is a new object with new numbers. */
function samePlace(a: Placement, b: Placement | undefined): boolean {
  return b !== undefined && a.x === b.x && a.y === b.y && a.z === b.z && a.yaw === b.yaw && a.scale === b.scale
}

/**
 * How long a hand holding a drawn rod may be out of tracking before the rod is let go, seconds (25 Sep
 * 2026). Long enough for a one- or two-frame dropout mid-pull, short enough that a yank-and-release
 * the cameras lost still plunges on the same beat. `CabinetRig.PlungerLostGraceSeconds` is the twin.
 */
const PLUNGER_LOST_GRACE_SECONDS = 0.12
